import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  BottomNavigation, 
  BottomNavigationAction, 
  Box,
  Badge,
  Avatar
} from '@mui/material';
import {
  Home,
  Store,
  Event,
  Chat,
  AccountCircle
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContextNew';
import { useChatUnread } from '../contexts/ChatUnreadContext';
import SignUpPromptModal from './SignUpPromptModal';

const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isGuest } = useAuth();
  const { unreadCount: chatUnreadTotal } = useChatUnread();
  
  // Debug logging
//  console.log('🔽 BottomNav component - user data:', user);
//  console.log('🔽 BottomNav component - profilePhotoUrl:', user?.profilePhotoUrl);

  const [value, setValue] = React.useState(0);
  const [showSignUpPrompt, setShowSignUpPrompt] = React.useState(false);
  const [restrictedFeature, setRestrictedFeature] = React.useState('');

  // Guest mode handlers
  const handleGuestRestriction = (featureName) => {
    setRestrictedFeature(featureName);
    setShowSignUpPrompt(true);
  };

  // Map routes to navigation values
  React.useEffect(() => {
    const path = location.pathname;
    if (path.includes('/home') || path.includes('/old-home')) setValue(0);
    else if (path.includes('/store')) setValue(1);
    else if (path.includes('/bookings')) setValue(2);
    else if (path.includes('/chat')) setValue(3);
    else if (path.includes('/profile')) setValue(4);
  }, [location.pathname]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    switch (newValue) {
      case 0:
        navigate('/home');
        break;
      case 1:
        navigate('/store');
        break;
      case 2:
        if (isGuest) {
          handleGuestRestriction('bookings');
          setValue(value); // Keep current value
          return;
        }
        // If we're already on bookings page, dispatch a custom event to go back to listing
        if (location.pathname === '/bookings') {
          window.dispatchEvent(new CustomEvent('navigateToBookingListing'));
        } else {
          navigate('/bookings');
        }
        break;
      case 3:
        if (isGuest) {
          handleGuestRestriction('chat');
          setValue(value); // Keep current value
          return;
        }
        navigate('/chat');
        break;
      case 4:
        if (isGuest) {
          handleGuestRestriction('profile');
          setValue(value); // Keep current value
          return;
        }
        navigate('/profile');
        break;
      default:
        navigate('/home');
    }
  };

  return (
    <Box
      key={user?.profilePhotoUrl}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        backgroundColor: '#1a237e',
        borderTop: '3px solid #FFD700',
        boxShadow: '0 -2px 10px rgba(0,0,0,0.2)',
        height: '60px'
      }}
    >
      <BottomNavigation
        value={value}
        onChange={handleChange}
        sx={{
          backgroundColor: '#1a237e',
          '& .MuiBottomNavigationAction-root': {
            minWidth: 'auto',
            padding: '6px 12px 8px',
            color: 'rgba(255,255,255,0.7)',
            transition: 'all 0.2s',
            '&.Mui-selected': {
              color: '#FFD700',
              backgroundColor: 'rgba(255, 215, 0, 0.1)',
            },
            '&:hover': {
              color: '#FFD700',
              backgroundColor: 'rgba(255, 215, 0, 0.1)',
            },
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.65rem',
            fontWeight: 500,
            '&.Mui-selected': {
              fontSize: '0.65rem',
              fontWeight: 600,
              color: '#FFD700',
            },
          },
        }}
      >
        <BottomNavigationAction
          label="Home"
          icon={<Home />}
        />
        <BottomNavigationAction
          label="Store"
          icon={<Store />}
        />
        <BottomNavigationAction
          label="Bookings"
          icon={<Event />}
        />
        <BottomNavigationAction
          label="Chat"
          icon={
            !isGuest && chatUnreadTotal > 0 ? (
              <Badge color="error" max={99} badgeContent={chatUnreadTotal} showZero={false}>
                <Chat />
              </Badge>
            ) : (
              <Chat />
            )
          }
        />
        <BottomNavigationAction
          label="Profile"
          icon={
            user && (user.profilePhotoUrl || user.avatar) ? (
              <Avatar
                src={user.profilePhotoUrl || user.avatar}
                sx={{
                  width: 24,
                  height: 24,
                  border: '2px solid rgba(255,255,255,0.3)'
                }}
              />
            ) : (
              <AccountCircle />
            )
          }
        />
      </BottomNavigation>

      {/* Sign Up Prompt Modal for Guest Users */}
      <SignUpPromptModal
        open={showSignUpPrompt}
        onClose={() => setShowSignUpPrompt(false)}
        featureName={restrictedFeature}
      />
    </Box>
  );
};

export default BottomNav; 